import { cn } from "@/lib/utils";
import {
  formatGradeBadge,
  formatGradingBadge,
  gradeBadgeTier,
  type GradingDisplayInput,
} from "@/lib/grading/companies";

const TIER_CLASS: Record<ReturnType<typeof gradeBadgeTier>, string> = {
  pristine:
    "border-gold/50 bg-gold-gradient text-gold-foreground shadow-[0_6px_20px_-8px_rgba(212,175,55,0.8)]",
  gem: "border-gold/45 bg-gold/15 text-gold",
  mint: "border-primary/40 bg-primary/10 text-primary",
  "near-mint": "border-sky-500/35 bg-sky-500/10 text-sky-300",
  excellent: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  good: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  poor: "border-border/60 bg-muted/40 text-muted-foreground",
  none: "border-border/60 bg-background/70 text-muted-foreground",
};

/**
 * Shared premium grade chip.
 *
 * - `variant="grade"` renders the grade alone ("PRISTINE 10", "GEM MINT 10").
 * - `variant="full"` renders the grader + product context line used on cards
 *   ("PSA 10 Gem Mint", "Raw / Ungraded", "Sealed Product").
 *
 * Colour tiers come from `gradeBadgeTier`, so every surface tints identically.
 */
export function GradeBadge({
  auction,
  variant = "full",
  size = "sm",
  className,
}: {
  auction: GradingDisplayInput;
  variant?: "full" | "grade";
  size?: "xs" | "sm" | "md";
  className?: string;
}) {
  const grade = auction.grade === "Other" ? auction.custom_grade : auction.grade;
  const tier = gradeBadgeTier(grade);
  const text = variant === "grade" ? formatGradeBadge(grade) : formatGradingBadge(auction);
  if (!text) return null;

  const sizing =
    size === "xs"
      ? "px-1.5 py-0.5 text-[10px]"
      : size === "md"
        ? "px-3 py-1 text-xs"
        : "px-2 py-0.5 text-[11px]";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border font-medium tracking-wide whitespace-nowrap backdrop-blur",
        variant === "grade" && "uppercase tracking-widest",
        sizing,
        TIER_CLASS[tier],
        className,
      )}
    >
      {text}
    </span>
  );
}
